// ========== GROCERY LIST SESSION MANAGER ==========
// Manages unique grocery list IDs and checkout notifications
import { database, ref, set, onValue, off, remove } from '../pages/Auth/db/firebase';

// Generate unique list ID in format: 241025212338 ([YY-MM-DD]-[HHmmss])
export function generateListId() {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2); // 24
  const mm = String(now.getMonth() + 1).padStart(2, '0'); // 10
  const dd = String(now.getDate()).padStart(2, '0'); // 25
  const hh = String(now.getHours()).padStart(2, '0'); // 21
  const min = String(now.getMinutes()).padStart(2, '0'); // 23
  const ss = String(now.getSeconds()).padStart(2, '0'); // 38
  
  return `${yy}${mm}${dd}${hh}${min}${ss}`;
}

// Get current list ID from localStorage or create new one
// List ID persists as long as there are items in the cart
export function getCurrentListId(facilityId) {
  const storageKey = `grocery-list-id-${facilityId}`;
  let listId = localStorage.getItem(storageKey);
  
  // Check if cart has items - if not, create new ID
  const cartStorageKey = `grocery-cart-${facilityId}`;
  try {
    const cartData = localStorage.getItem(cartStorageKey);
    if (cartData) {
      const cart = JSON.parse(cartData);
      if (cart.items && cart.items.length === 0) {
        // Cart is empty, generate new ID
        listId = generateListId();
        localStorage.setItem(storageKey, listId);
        return listId;
      }
    }
  } catch (e) {
    console.error('Error checking cart:', e);
  }
  
  if (!listId) {
    listId = generateListId();
    localStorage.setItem(storageKey, listId);
  }
  
  return listId;
}

// Store list session in Firebase (for checkout tracking)
export async function registerListSession(facilityId, listId, items) {
  try {
    const sessionRef = ref(database, `groceryListSessions/${facilityId}/${listId}`);
    await set(sessionRef, {
      items: items.map(item => ({
        name: item.name,
        baseId: item.baseId || item.key,
        quantity: item.quantity,
        price: item.price
      })),
      createdAt: Date.now(),
      status: 'active'
    });
    return true;
  } catch (error) {
    console.error('Error registering list session:', error);
    return false;
  }
}

// Listen for checkout completion
export function listenForCheckout(facilityId, listId, onCheckoutComplete) {
  const sessionRef = ref(database, `groceryListSessions/${facilityId}/${listId}`);
  
  const handler = (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      if (data.status === 'completed' && data.receipt) {
        onCheckoutComplete(data.receipt);
      }
    }
  };
  
  onValue(sessionRef, handler);
  
  // Return cleanup function
  return () => off(sessionRef, 'value', handler);
}

// Mark list as completed (called by cashier after checkout)
export async function markListCompleted(facilityId, listId, receiptData) {
  try {
    const sessionRef = ref(database, `groceryListSessions/${facilityId}/${listId}`);
    await set(sessionRef, {
      status: 'completed',
      completedAt: Date.now(),
      receipt: receiptData
    });
    return true;
  } catch (error) {
    console.error('Error marking list completed:', error);
    return false;
  }
}

// Clear list session (after receipt is shown)
export async function clearListSession(facilityId, listId) {
  try {
    const sessionRef = ref(database, `groceryListSessions/${facilityId}/${listId}`);
    await remove(sessionRef);
    
    // Remove from localStorage
    const storageKey = `grocery-list-id-${facilityId}`;
    localStorage.removeItem(storageKey);
    
    return true;
  } catch (error) {
    console.error('Error clearing list session:', error);
    return false;
  }
}

// Extract list ID from QR code URL
export function extractListIdFromUrl(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('listId');
  } catch {
    return null;
  }
}
// ========== GROCERY LIST SESSION MANAGER ENDS ==========
